import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface TextInputOverlayProps {
  position: { x: number; y: number };
  onSubmit: (text: string) => void;
  onCancel: () => void;
  style: {
    color: string;
    fontSize: number;
    fontFamily: string;
    backgroundColor?: string;
  };
  initialValue?: string;
  onDelete?: () => void;
}

const TextInputOverlay: React.FC<TextInputOverlayProps> = ({
  position,
  onSubmit,
  onCancel,
  style,
  initialValue = '',
  onDelete
}) => {
  const [text, setText] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the input when component mounts
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = () => {
    onSubmit(text);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit();
    } else if (event.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        zIndex: 1000,
        // transform: 'translate(-50%, -50%)', // Eliminado para alinear arriba a la izquierda
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: '8px',
          borderRadius: '8px',
          backgroundColor: '#fff',
          border: `2px solid ${style.color}`,
          minWidth: 200,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your annotation..."
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: style.fontSize,
                fontFamily: style.fontFamily,
                color: style.color,
                backgroundColor: style.backgroundColor || '#fff',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
                '& input': {
                  fontSize: style.fontSize,
                  fontFamily: style.fontFamily,
                  color: style.color,
                },
                '& input::placeholder': {
                  fontSize: style.fontSize * 0.8,
                  fontFamily: style.fontFamily,
                  color: style.color + '80', // Add transparency
                },
              },
            }}
          />
          <IconButton
            size="small"
            onClick={handleSubmit}
            sx={{
              color: 'success.main',
              '&:hover': {
                backgroundColor: 'success.light',
                color: 'white',
              },
            }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onDelete ? onDelete : onCancel}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'white',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default TextInputOverlay; 